/**
 * ART ENERGI (INSPIYAH) Feature Flags Configuration
 * 
 * Controls feature visibility across environments:
 * - Development: All features enabled
 * - Test: All features enabled (SHOW_ALL_FEATURES=true)
 * - Production MVP: Core features only (SHOW_ALL_FEATURES=false)
 * 
 * Repository: CT Digital Hub / ART ENERGI Portfolio
 * Domain: artenergi.org (formerly ct-digital-hub)
 * Last Updated: January 5, 2026
 */

// Environment detection
const environment = process.env.REACT_APP_ENVIRONMENT || 'development';
const showAllFeatures = process.env.REACT_APP_SHOW_ALL_FEATURES === 'true';
const isProduction = environment === 'production';
const isTest = environment === 'test';
const isDevelopment = environment === 'development';

/**
 * Feature Flags
 * 
 * TRUE = Feature is visible
 * FALSE = Feature is hidden
 * 
 * Production MVP uses showAllFeatures flag to control visibility
 */
export const FEATURES = {
  // ========================================
  // ✅ MVP FEATURES (ALWAYS SHOW IN PRODUCTION)
  // Core Portfolio & Identity
  // ========================================
  HOME: true,
  HERO: true,
  ABOUT: true,
  STORYTELLER: true,
  SKILLS: true,
  EXPERIENCE: true,
  SEEDS_OF_WISDOM: true,                    // NEW: Video section between Experience & Projects
  PROJECTS: true,
  CONTACT: true,
  FOOTER: true,
  
  // Spiritual & Branding Elements
  WELCOME_GATE: true,                       // Dual consent (Ecclesiastical + DPA)
  YAHFFIRMATIONS: true,                     // 7-day daily affirmations section
  INSPIYAH_MAIL: true,                      // Email subscription with consent
  
  // Legal & Governance (Always visible)
  PRIVACY_POLICY: true,
  DATA_PROCESSING_ADDENDUM: true,           // NEW: Comprehensive DPA
  TERMS_OF_SERVICE: true,
  AI_USAGE_POLICY: true,
  DISCLAIMER: true,
  
  // Analytics & Admin (Password protected)
  ANALYTICS_DASHBOARD: true,                // NEW: Password-protected analytics
  ANALYTICS_WELCOME_GATE: true,             // View tracking
  ANALYTICS_SUBSCRIBERS: true,              // Subscriber list
  ANALYTICS_MESSAGES: true,                 // NEW: Contact messages with notifications
  
  // Videos
  HERO_VIDEO: true,                         // NEW: Background video in Hero
  SEEDS_VIDEO: true,                        // NEW: Featured video with controls
  
  // Social/Professional Links
  LINKEDIN_PROFILE: true,                   // NEW: LinkedIn in Contact & Footer
  
  // ========================================
  // ❌ FUTURE FEATURES (HIDE IN PRODUCTION)
  // Show only in test/development environments
  // ========================================
  
  // Hero Section Elements (Hidden)
  FREE_DOWNLOAD: true,                      // "Comedy Hour: A Nation's Prayer" download button
  EPIIC_HERO_MODAL: true,                   // Lead capture modal for free download
  
  // Content & Resources (Hidden)
  RESOURCES: showAllFeatures,               // Resources page/section
  BLOG_CARDS: showAllFeatures,              // Blog post cards (verbiage only shown)
  
  // Blog & Content Hub (Hidden)
  INSPIYAH_JOURNEY: showAllFeatures,        // Blog/content hub
  INSPIYAH_JOURNEY_ARTICLES: showAllFeatures, // Individual articles
  ARTICLE_1010_PORTAL: showAllFeatures,     // Specific article routes
  
  // E-Commerce (Hidden - Shop broken, deferred)
  SHOP: showAllFeatures,                    // INSPIYAH Culture store
  INSPIYAH_STORE: showAllFeatures,          // Store component (currently non-functional)
  PRODUCT_CATALOG: showAllFeatures,         // Product listings
  SHOPPING_CART: showAllFeatures,           // Cart functionality
  
  // Community & Engagement (Hidden)
  SOCIAL_MEDIA_HUB: showAllFeatures,        // Social media integration
  DONATIONS: showAllFeatures,               // Donation page
  TEACHER_REFERRALS: showAllFeatures,       // Teacher referral program
  
  // Admin & Management (Hidden)
  // Admin & Management (Internal tool - accessible on preview only, hidden in production)
  ADMIN_DASHBOARD: showAllFeatures,
  ADMIN_LOGIN: showAllFeatures,
  CONTENT_MANAGEMENT: showAllFeatures,
  HERO_EDITOR: showAllFeatures,
};

/**
 * Helper function to check if a feature is enabled
 * @param {string} featureName - Name of the feature from FEATURES object
 * @returns {boolean} - True if feature is enabled
 */
export const isFeatureEnabled = (featureName) => {
  if (!FEATURES.hasOwnProperty(featureName)) {
    console.warn(`⚠️ Feature "${featureName}" not found in FEATURES config`);
    return false;
  }
  return FEATURES[featureName] === true;
};

/**
 * Get list of enabled features
 * @returns {string[]} - Array of enabled feature names
 */
export const getEnabledFeatures = () => {
  return Object.keys(FEATURES).filter(key => FEATURES[key] === true);
};

/**
 * Get list of disabled features
 * @returns {string[]} - Array of disabled feature names
 */
export const getDisabledFeatures = () => {
  return Object.keys(FEATURES).filter(key => FEATURES[key] === false);
};

/**
 * Environment information (for debugging)
 */
export const ENV_INFO = {
  environment: environment,
  showAllFeatures: showAllFeatures,
  isProduction: isProduction,
  isTest: isTest,
  isDevelopment: isDevelopment,
  totalFeatures: Object.keys(FEATURES).length,
  enabledFeatures: getEnabledFeatures().length,
  disabledFeatures: getDisabledFeatures().length,
};

// Console logging (only in development/test)
if (!isProduction) {
  console.log('🔧 INSPIYAH Feature Flags Loaded');
  console.log('📍 Environment:', ENV_INFO.environment);
  console.log('🎯 Show All Features:', ENV_INFO.showAllFeatures);
  console.log('✅ Enabled Features:', ENV_INFO.enabledFeatures);
  console.log('❌ Disabled Features:', ENV_INFO.disabledFeatures);
  
  if (isDevelopment) {
    console.log('💡 Tip: Set REACT_APP_SHOW_ALL_FEATURES=false in .env to test production mode');
  }
}

/**
 * Feature Groups (for organized management)
 */
export const FEATURE_GROUPS = {
  MVP_CORE: [
    'HOME',
    'HERO',
    'ABOUT', 
    'STORYTELLER',
    'SKILLS',
    'EXPERIENCE',
    'SEEDS_OF_WISDOM',
    'PROJECTS',
    'CONTACT',
    'FOOTER',
  ],
  
  SPIRITUAL_BRANDING: [
    'WELCOME_GATE',
    'YAHFFIRMATIONS',
    'INSPIYAH_MAIL',
    'HERO_VIDEO',
    'SEEDS_VIDEO',
  ],
  
  GOVERNANCE_LEGAL: [
    'PRIVACY_POLICY',
    'DATA_PROCESSING_ADDENDUM',
    'TERMS_OF_SERVICE',
    'AI_USAGE_POLICY',
    'DISCLAIMER',
  ],
  
  ANALYTICS_ADMIN: [
    'ANALYTICS_DASHBOARD',
    'ANALYTICS_WELCOME_GATE',
    'ANALYTICS_SUBSCRIBERS',
    'ANALYTICS_MESSAGES',
  ],
  
  PROFESSIONAL: [
    'LINKEDIN_PROFILE',
    'CONTACT',
    'EXPERIENCE',
    'PROJECTS',
  ],
  
  CONTENT_HUB: [
    'INSPIYAH_JOURNEY',
    'INSPIYAH_JOURNEY_ARTICLES',
    'ARTICLE_1010_PORTAL',
    'RESOURCES',
    'BLOG_CARDS',
  ],
  
  ECOMMERCE: [
    'SHOP',
    'INSPIYAH_STORE',
    'PRODUCT_CATALOG',
    'SHOPPING_CART',
  ],
  
  COMMUNITY: [
    'SOCIAL_MEDIA_HUB',
    'DONATIONS',
    'TEACHER_REFERRALS',
  ],
  
  ADMIN: [
    'ADMIN_DASHBOARD',
    'ADMIN_LOGIN',
    'CONTENT_MANAGEMENT',
    'HERO_EDITOR',
  ],
};

/**
 * MVP Feature Count & Status
 */
export const MVP_STATS = {
  totalMVPFeatures: 23,
  newFeaturesAdded: 8,
  lastUpdated: 'January 5, 2026',
  productionDomain: 'artenergi.org',
  stagingDomain: 'ct-digital-hub.preview.emergentagent.com',
};

/**
 * New Features Added in Latest Update
 */
export const NEW_FEATURES = [
  'SEEDS_OF_WISDOM',              // Video section between Experience & Projects
  'DATA_PROCESSING_ADDENDUM',     // Comprehensive DPA legal page
  'ANALYTICS_DASHBOARD',          // Password-protected analytics
  'ANALYTICS_MESSAGES',           // Contact message notifications
  'HERO_VIDEO',                   // Background video in Hero section
  'SEEDS_VIDEO',                  // Featured video with controls
  'LINKEDIN_PROFILE',             // Professional networking links
  'WELCOME_GATE',                 // Dual consent system (updated)
];

export default FEATURES;
